import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import Select from "react-select";
import axios from "axios";
import './ZonesAndDistrictWiseShifts.css';
import { shiftListAssign, shiftsAssignmentSubmit, shiftsGets, getDesignationsByRoleId } from "../../../services/shiftsService";
import { getAuditLog } from "../../../utils/calculateDaysInMonthUtils";
import { selectStyles } from "../../../assets/Styles/selectStyle";
import { formatTime } from "../../../utils/validations";
import { getZonesAll } from "../../../services/monthlyAttendanceService";

const ZonesAndDistrictWiseShifts = () => {
    const [zoneOptions, setZoneOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [selectedZone, setSelectedZone] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);

    const [employeeList, setEmployeeList] = useState([]);
    const [shiftList, setShiftList] = useState([]);
    const [editingRows, setEditingRows] = useState(new Set());
    const [editedData, setEditedData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [loading, setLoading] = useState({ zones: true, districts: false, employees: false });
    const [designationOptions, setDesignationOptions] = useState([]);
    const [designation, setDesignation] = useState("");

    const roleId = Number(localStorage.getItem("roleId"));

    // Fetch all zones on mount
    useEffect(() => {
        const fetchZones = async () => {
            try {
                setLoading(prev => ({ ...prev, zones: true }));
                const data = await getZonesAll();
                const zones = data.map(zone => ({
                    value: zone.zoneId,
                    label: zone.zoneName,
                    districts: zone.districts || []
                }));
                setZoneOptions(zones);
            } catch (error) {
                console.error("Error fetching zones:", error);
                setMessage({ text: "Failed to load zones", type: "error" });
            } finally {
                setLoading(prev => ({ ...prev, zones: false }));
            }
        };
        fetchZones();
    }, []);

    // When zone changes → update districts dropdown
    useEffect(() => {
        if (selectedZone) {
            const zone = zoneOptions.find(z => z.value === selectedZone);
            if (zone && zone.districts) {
                const districts = zone.districts.map(d => ({
                    value: d.districtId,
                    label: d.districtName
                }));
                setDistrictOptions(districts);
                setSelectedDistrict(null); // Reset district
                setEmployeeList([]);
                setEditedData({});
                setEditingRows(new Set());
            }
        } else {
            setDistrictOptions([]);
            setSelectedDistrict(null);
            setEmployeeList([]);
        }
    }, [selectedZone, zoneOptions]);

    // Fetch employees when district is selected

    const fetchEmployees = async () => {
        if (!selectedDistrict) {
            setEmployeeList([]);
            return;
        }

        setLoading(prev => ({ ...prev, employees: true }));
        try {
            const response = await shiftListAssign(selectedDistrict, designation);

            setEmployeeList(response.data || []);
            setEditedData({});
            setEditingRows(new Set());
        } catch (error) {
            console.error("Error fetching employees:", error);
            setEmployeeList([]);
            setMessage({ text: "Failed to load employees", type: "error" });
        } finally {
            setLoading(prev => ({ ...prev, employees: false }));
        }
    };



    useEffect(() => {
        if (selectedDistrict) fetchEmployees();
    }, [selectedDistrict, designation]);   // added designation

    const loadDesignations = async () => {
        try {
            const data = await getDesignationsByRoleId(roleId);

            const list = data.map(d => ({
                value: d.id,
                label: d.designationName
            }));

            setDesignationOptions(list);
        } catch (error) {
            console.error("Error loading designations:", error);
        }
    };

    useEffect(() => {
        if (roleId) loadDesignations();
    }, [roleId]);

    // Load available shifts once
    useEffect(() => {
        const loadShifts = async () => {
            try {
                const res = await shiftsGets();
                setShiftList(res.data || []);
            } catch (err) {
                console.log("Error loading shifts", err);
            }
        };
        loadShifts();
    }, []);

    const startEdit = (id) => {
        setEditingRows(prev => new Set(prev).add(id));
        if (!editedData[id]) {
            const emp = employeeList.find(e => e.id === id);
            setEditedData(prev => ({
                ...prev,
                [id]: {
                    shiftId: emp.shiftId,
                    shiftStartDate: emp.shiftStartDate,
                    shiftEndDate: emp.shiftEndDate,
                }
            }));
        }
    };

    const cancelEdit = (id) => {
        setEditingRows(prev => { const s = new Set(prev); s.delete(id); return s; });
        setEditedData(prev => { const d = { ...prev }; delete d[id]; return d; });
    };

    const handleChange = (id, field, value) => {
        setEditedData(prev => ({
            ...prev,
            [id]: { ...prev[id], [field]: value }
        }));
    };

    const handleBulkSubmit = async () => {
        if (editingRows.size === 0) return;

        setIsSubmitting(true);
        const list = Array.from(editingRows).map(id => {
            const data = editedData[id];
            const emp = employeeList.find(e => e.id === id);
            return {
                postingId: emp.postingId,
                shiftId: parseInt(data.shiftId),
                shiftStartDate: data.shiftStartDate,
                shiftEndDate: data.shiftEndDate
            };
        });

        const payload = { auditLog: getAuditLog(), list };

        try {
            const response = await shiftsAssignmentSubmit(payload);
            if (response.status === 200 || response.status === 201) {
                const updatedList = employeeList.map(emp => {
                    if (editedData[emp.id]) {
                        const updated = editedData[emp.id];
                        const newShift = shiftList.find(s => s.shiftId === parseInt(updated.shiftId));
                        return {
                            ...emp,
                            shiftId: parseInt(updated.shiftId),
                            shiftStartDate: updated.shiftStartDate,
                            shiftEndDate: updated.shiftEndDate,
                            shiftName: newShift?.shiftName || emp.shiftName,
                        };
                    }
                    return emp;
                });
                setEmployeeList(updatedList);
                setEditingRows(new Set());
                setEditedData({});
                setMessage({ text: "All changes saved successfully!", type: "success" });
            }
        } catch (error) {
            setMessage({ text: "Failed to save changes", type: "error" });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Auto close message
    useEffect(() => {
        if (message.text) {
            const timer = setTimeout(() => setMessage({ text: "", type: "" }), 3000);
            return () => clearTimeout(timer);
        }
    }, [message.text]);
    const handleClear = () => {
        setSelectedZone("");
        setSelectedDistrict("");
        setDesignation("");
    }
    return (
        <div className="zones-district-container-shift">
            <h2 className="page-title">PMDC / PMO Shifts Assignment</h2>

            <div className="filters-section">
                <div className="filter-group">
                    <label>Zone :</label>
                    <Select
                        options={zoneOptions}
                        value={zoneOptions.find(z => z.value === selectedZone) || null}
                        onChange={(opt) => setSelectedZone(opt ? opt.value : null)}
                        placeholder={loading.zones ? "Loading zones..." : "Select Zone"}
                        isClearable
                        isDisabled={loading.zones}
                        className="zone-select"
                        styles={selectStyles}
                    />
                </div>

                <div className="filter-group">
                    <label>District :</label>
                    <Select
                        options={districtOptions}
                        value={districtOptions.find(d => d.value === selectedDistrict) || null}
                        onChange={(opt) => setSelectedDistrict(opt ? opt.value : null)}
                        placeholder={
                            !selectedZone ? "First select a zone" :
                                districtOptions.length === 0 ? "No districts found" :
                                    "Select District"
                        }
                        isClearable
                        isDisabled={!selectedZone || districtOptions.length === 0}
                        className="district-select"
                    />
                </div>
                <div className="filter-group">
                    <label>Designation :</label>
                    <Select
                        options={designationOptions}
                        value={designationOptions.find(d => d.value === designation) || null}
                        onChange={(opt) => setDesignation(opt ? opt.value : "")}
                        placeholder="Select Designation"
                        isClearable
                        className="designation-select"
                        styles={selectStyles}
                        isDisabled={!selectedDistrict}  // only enable after district selected
                    />
                </div>

            </div>
            <div className="text-end btns-clear-shifts">
                <button
                    className="btn btn-danger mb-1 mt-1 disbal"
                    onClick={() => { handleClear() }}
                    disabled={!selectedZone}
                >
                    Clear
                </button>
            </div>

            {message.text && (
                <div className="confirm-overlay">
                    <div className={`success-message-popup ${message.type}`}>
                        {message.text}
                    </div>
                </div>
            )}

            <div className="table-wrapper-dist">

                <table className="shift-table-assign-dist">
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>Employee Name</th>
                            <th>Designation</th>
                            <th>Contact</th>
                            <th>From Date</th>
                            <th>To Date</th>
                            <th>Shift</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading.employees ? (
                            // Loading state
                            <tr>
                                <td colSpan="8" className="table-message loading">
                                    Loading employees...
                                </td>
                            </tr>
                        ) : !selectedZone ? (
                            // No zone selected
                            <tr>
                                <td colSpan="8" className="table-message info">
                                    Please select a Zone to continue
                                </td>
                            </tr>
                        ) : !selectedDistrict ? (
                            // Zone selected, but no district
                            <tr>
                                <td colSpan="8" className="table-message info">
                                    Please select a District to view employees
                                </td>
                            </tr>
                        ) : employeeList.length === 0 ? (
                            // District selected, but no employees
                            <tr>
                                <td colSpan="8" className="table-message no-data">
                                    No employees found in this district
                                </td>
                            </tr>
                        ) : (
                            // Finally, show employee rows
                            employeeList.map((emp, index) => {
                                const isEditing = editingRows.has(emp.id);
                                const currentData = editedData[emp.id] || emp;

                                return (
                                    <tr key={emp.id} className={isEditing ? "editing-row" : ""}>
                                        <td>{index + 1}</td>
                                        <td>{emp.fullName}</td>
                                        <td>{emp.designation}</td>
                                        <td>{emp.contactNumber}</td>

                                        {/* From Date */}
                                        <td>
                                            {isEditing ? (
                                                <input
                                                    type="date"
                                                    className="form-input"
                                                    value={format(new Date(currentData.shiftStartDate), "yyyy-MM-dd")}
                                                    onChange={(e) => handleChange(emp.id, "shiftStartDate", e.target.value)}
                                                    min={new Date().toISOString().split("T")[0]}
                                                />
                                            ) : (
                                                format(new Date(emp.shiftStartDate), "dd-MM-yyyy")
                                            )}
                                        </td>

                                        {/* To Date */}
                                        <td>
                                            {isEditing ? (
                                                <input
                                                    type="date"
                                                    className="form-input"
                                                    value={format(new Date(currentData.shiftEndDate), "yyyy-MM-dd")}
                                                    onChange={(e) => handleChange(emp.id, "shiftEndDate", e.target.value)}
                                                    min={currentData.shiftStartDate || format(new Date(emp.shiftStartDate), "yyyy-MM-dd")}
                                                />
                                            ) : (
                                                format(new Date(emp.shiftEndDate), "dd-MM-yyyy")
                                            )}
                                        </td>

                                        {/* Shift */}
                                        <td>
                                            {isEditing ? (
                                                <select
                                                    className="form-select"
                                                    value={currentData.shiftId || ""}
                                                    onChange={(e) => handleChange(emp.id, "shiftId", e.target.value)}
                                                >
                                                    <option value="">Select Shift</option>
                                                    {shiftList.map(s => (
                                                        <option key={s.shiftId} value={s.shiftId}>
                                                            {s.shiftName} ({s.startTime} - {s.endTime})
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className="shift-display">
                                                    {emp.shiftName && emp.shiftStartTime && emp.shiftEndTime
                                                        ? `${emp.shiftName}: ${formatTime(emp.shiftStartTime)} - ${formatTime(emp.shiftEndTime)}`
                                                        : "Not Assigned"}
                                                </span>
                                            )}
                                        </td>

                                        {/* Action */}
                                        <td className="action-buttons-dist-zone-shift">
                                            {isEditing ? (
                                                <button onClick={() => cancelEdit(emp.id)} className="btn-cancel-dist-zone-shift">
                                                    Cancel
                                                </button>
                                            ) : (
                                                <button onClick={() => startEdit(emp.id)} className="btn-edit-dist-zone-shift">
                                                    Edit
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <div className="bulk-submit-footer-dist">
                <div className="change-summary">
                    You are editing {editingRows.size} employee(s)
                </div>
                <button
                    onClick={handleBulkSubmit}
                    disabled={editingRows.size === 0 || isSubmitting}
                    className={`btn-submit-all-dist ${editingRows.size > 0 ? 'active' : ''}`}
                >
                    {isSubmitting ? "Submitting..." : `Submit All Changes (${editingRows.size})`}
                </button>
            </div>

        </div>
    );
};

export default ZonesAndDistrictWiseShifts;